import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import './App.css';
import { AuthProvider as AuthContextProvider, useAuth } from './context/AuthContext';

import Navbar from './components/Navbar/Navbar';
import Loading from './components/Loading/Loading';
import Landing from './components/Landing/Landing';
import FloatingChatbot from './components/Chatbot/FloatingChatbot';

import { LandingProvider, useLanding } from './context/LandingContext';

const Home = lazy(() => import('./pages/Home/Home'));
const Accommodation = lazy(() => import('./pages/Accommodation/Accommodation'));
const AccommodationAlt = lazy(() => import('./pages/Accommodation/AccommodationAlt'));
const Exhibitions = lazy(() => import('./pages/Exhibitions/Exhibitions'));
const CES = lazy(() => import('./pages/CES/CES'));
const Lectures = lazy(() => import('./pages/Lectures/Lectures'));
const Competitions = lazy(() => import('./pages/Competitions/Competitions'));
const CompiInternal = lazy(() => import('./pages/Competitions/components/CompiInternal'));
const CompiForm = lazy(() => import('./pages/Competitions/components/CompiForm'));
const Workshops = lazy(() => import('./pages/Workshops/Workshops'));
const WorkshopInternal = lazy(() => import('./pages/Workshops/components/WorkshopInternal'));
const WorkshopForm = lazy(() => import('./pages/Workshops/components/WorkshopForm'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const ContactUs = lazy(() => import('./pages/ContactUs/ContactUs'));
const Technoholix = lazy(() => import('./pages/Technoholix/Technoholix'));
const SocialInitiative = lazy(() => import('./pages/SocialInitiative/SocialInitiative'));
const IgniteAlt = lazy(() => import('./pages/Ignite/IgniteAlt'));
const History = lazy(() => import('./pages/History/History'));
const Recognition = lazy(() => import('./pages/Recognition/Recognition'));
const Legals = lazy(() => import('./pages/Legals/Legals'));
const Media = lazy(() => import('./pages/Media/Media'));
const Gallery = lazy(() => import('./pages/Gallery/Gallery'));



// const LoadingFallback = () => (
//   <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'black' }}>
//     <div style={{ width: '40px', height: '40px', border: '4px solid rgba(0,0,0,0.05)', borderLeftColor: '#6A00DB', borderRadius: '50%', animation: 'app-spin 1s linear infinite' }} />
//     <style>{`@keyframes app-spin { to { transform: rotate(360deg); } }`}</style>
//   </div>
// );

const LoadingFallback = () => <Loading />;

const NavbarLayout = () => (
  <>
    <Navbar />
    <Suspense fallback={<LoadingFallback />}>
      <Outlet />
    </Suspense>
  </>
);

// Fix 2: RequireAuth only blocks protected pages, not the whole app
const RequireAuth = () => {
  const { currentUser, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (!currentUser) return <Navigate to="/competitions" replace />;
  return <Outlet />;
};

// WaitAuth waits for auth to initialize but doesn't block unauthenticated users
const WaitAuth = () => {
  const { loading } = useAuth();
  if (loading) return <LoadingFallback />;
  return <Outlet />;
};

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    // Disable browser's default scroll restoration so back/forward starts at the top
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.body.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };

    scrollToTop();
    const timeoutId = setTimeout(scrollToTop, 0);
    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search, location.key]);

  return null;
};

function AppContent() {
  const { showLanding, setShowLanding } = useLanding();

  const handleLandingComplete = () => {
    sessionStorage.setItem('hasVisitedLanding', 'true');
    setShowLanding(false);
  };

  return (
    <div style={{ width: '100%', height: '100vh', margin: 0, padding: 0 }}>
      {showLanding && <Landing onComplete={handleLandingComplete} />}
      <Router>
        <ScrollToTop />
        <AuthContextProvider>
          {/* No Suspense here anymore */}
          <Routes>
            <Route element={<NavbarLayout />}>
              <Route path="/" element={<Suspense fallback={<LoadingFallback />}><Home /></Suspense>} />
              <Route path="/exhibitions" element={<Suspense fallback={<LoadingFallback />}><Exhibitions /></Suspense>} />
              {/* <Route path="/accommodation" element={<Suspense fallback={<LoadingFallback />}><Accommodation /></Suspense>} /> */}
              <Route path="/accommodation" element={<Suspense fallback={<LoadingFallback />}><AccommodationAlt /></Suspense>} />
              <Route path="/competitions" element={<Suspense fallback={<LoadingFallback />}><Competitions /></Suspense>} />
              <Route path="/ces" element={<Suspense fallback={<LoadingFallback />}><CES /></Suspense>} />
              <Route path="/workshops" element={<Suspense fallback={<LoadingFallback />}><Workshops /></Suspense>} />
              <Route path="/lectures" element={<Suspense fallback={<LoadingFallback />}><Lectures /></Suspense>} />
              <Route path="/profile" element={<Suspense fallback={<LoadingFallback />}><Profile /></Suspense>} />
              <Route path="/contactus" element={<Suspense fallback={<LoadingFallback />}><ContactUs /></Suspense>} />
              <Route path="/technoholix" element={<Suspense fallback={<LoadingFallback />}><Technoholix /></Suspense>} />
              <Route path="/social-initiative" element={<Suspense fallback={<LoadingFallback />}><SocialInitiative /></Suspense>} />
              <Route path="/ignite" element={<Suspense fallback={<LoadingFallback />}><IgniteAlt /></Suspense>} />
              <Route path="/history" element={<Suspense fallback={<LoadingFallback />}><History /></Suspense>} />
              <Route path="/recognition" element={<Suspense fallback={<LoadingFallback />}><Recognition /></Suspense>} />
              <Route path="/legals" element={<Suspense fallback={<LoadingFallback />}><Legals /></Suspense>} />
              <Route path="/media" element={<Suspense fallback={<LoadingFallback />}><Media /></Suspense>} />
              <Route path="/gallery" element={<Suspense fallback={<LoadingFallback />}><Gallery /></Suspense>} />
              <Route path="/competitions/:compiId" element={<Suspense fallback={<LoadingFallback />}><CompiInternal /></Suspense>} />
              <Route path="/workshops/:workshopId" element={<Suspense fallback={<LoadingFallback />}><WorkshopInternal /></Suspense>} />
              {/* Protected: requires Google sign-in */}
              <Route element={<RequireAuth />}>
                <Route path="/competitions/:compiId/register" element={<Suspense fallback={<LoadingFallback />}><CompiForm /></Suspense>} />
                <Route path="/workshops/:workshopId/register" element={<Suspense fallback={<LoadingFallback />}><WorkshopForm /></Suspense>} />
              </Route>
            </Route>
          </Routes>
          {!showLanding && <FloatingChatbot />}
        </AuthContextProvider>
      </Router>
    </div>
  );
}

export default function App() {
  return (
    <LandingProvider>
      <AppContent />
    </LandingProvider>
  );
}